import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { Box, Stack, Typography, CircularProgress, Chip, Skeleton, Button } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import NotificationCard from '../components/notificationCard';
import PageHeader from 'components/v2/common/pageHeader';
import Result from 'components/v2/result';

import {
    useNotifications,
    useNotificationCategories,
    type NotificationCategoryFilter,
} from 'hooks/useNotifications';
import { RootState } from 'redux/store';
import type { NotificationInterface } from 'types';
import { toTitleCase } from 'utils/v2/text';

const chipSx = (active: boolean) => ({
    fontWeight: active ? 600 : 500,
    fontSize: { xs: '0.7rem', sm: '0.75rem' },
    flexShrink: 0,
    height: { xs: 26, sm: 30 },
    maxWidth: { xs: 140, sm: 220 },
    borderRadius: 1.5,
    '& .MuiChip-label': {
        px: { xs: 1, sm: 1.25 },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});

const Notifications = () => {
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.entity);
    const userId = user?.id || 0;
    const [category, setCategory] = useState<NotificationCategoryFilter>('all');

    const { data: categories = [], isLoading: isLoadingCategories } = useNotificationCategories();

    const {
        data: notifications = [],
        isLoading,
        isFetching,
        isError,
    } = useNotifications(userId, { category });

    const handleNotificationClick = (notification: NotificationInterface) => {
        if (notification.url) {
            navigate(notification.url);
        }
    };

    const showInitialLoading = isLoading && notifications.length === 0;
    const categoryLabel = category === 'all' ? 'all' : toTitleCase(category);
    const showCount = !showInitialLoading && !isError;

    return (
        <Stack
            sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                overflow: 'hidden',
                bgcolor: 'background.default',
            }}
        >
            {/* ── Fixed top: PageHeader + filter + count ── */}
            <Box
                sx={{
                    flexShrink: 0,
                    width: '100%',
                    px: { xs: 1.5, sm: 2.5, md: 3 },
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 1, sm: 1.25 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <PageHeader
                    title="Notifications"
                    description="Stay updated with your immigration journey"
                    sx={{ mb: { xs: 1.5, sm: 2 } }}
                />

                {/* Category filter */}
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{
                            alignItems: 'center',
                            overflowX: 'auto',
                            flex: 1,
                            minWidth: 0,
                            py: 0.25,
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <Chip
                            label="All"
                            size="small"
                            clickable
                            variant={category === 'all' ? 'filled' : 'outlined'}
                            color={category === 'all' ? 'primary' : 'default'}
                            onClick={() => setCategory('all')}
                            sx={chipSx(category === 'all')}
                        />

                        {isLoadingCategories
                            ? Array.from({ length: 3 }).map((_, i) => (
                                  <Skeleton
                                      key={i}
                                      variant="rounded"
                                      width={64}
                                      height={26}
                                      sx={{ flexShrink: 0, borderRadius: 1.5 }}
                                  />
                              ))
                            : categories.map((cat) => {
                                  const active = category === cat;
                                  return (
                                      <Chip
                                          key={cat}
                                          label={toTitleCase(cat)}
                                          size="small"
                                          clickable
                                          variant={active ? 'filled' : 'outlined'}
                                          color={active ? 'primary' : 'default'}
                                          onClick={() => setCategory(cat)}
                                          sx={chipSx(active)}
                                          title={toTitleCase(cat)}
                                      />
                                  );
                              })}
                    </Stack>

                    {isFetching && !isLoading && (
                        <CircularProgress
                            size={14}
                            sx={{ flexShrink: 0, color: 'text.disabled' }}
                        />
                    )}
                </Stack>

                {/* Result count below filter */}
                {showCount && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            mt: 1,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                    >
                        {notifications.length === 0
                            ? 'No results'
                            : `${notifications.length} notification${
                                  notifications.length === 1 ? '' : 's'
                              }`}
                        {category !== 'all' && (
                            <>
                                {' · '}
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    {categoryLabel}
                                </Box>
                            </>
                        )}
                    </Typography>
                )}
            </Box>

            {/* ── Full remaining space: scrollable notification content ── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    WebkitOverflowScrolling: 'touch',
                    opacity: isFetching && !isLoading ? 0.7 : 1,
                    transition: 'opacity 0.15s ease',
                }}
            >
                {showInitialLoading ? (
                    <Stack sx={{ px: { xs: 1.5, sm: 2.5, md: 3 }, py: { xs: 1.25, sm: 2 } }}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Stack
                                key={i}
                                direction="row"
                                spacing={1.5}
                                sx={{
                                    py: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Skeleton variant="circular" width={36} height={36} />
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                    <Skeleton width="85%" height={18} />
                                    <Skeleton width="35%" height={14} sx={{ mt: 0.75 }} />
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                ) : isError ? (
                    <Result
                        icon={<NotificationsIcon sx={{ fontSize: 48, color: 'error.main' }} />}
                        title="Couldn't load notifications"
                        description="Something went wrong while fetching your notifications. Please try again later."
                    />
                ) : notifications.length === 0 ? (
                    <Result
                        icon={<NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                        title={
                            category === 'all'
                                ? 'No notifications yet'
                                : `No ${categoryLabel} notifications`
                        }
                        description={
                            category === 'all'
                                ? "We'll notify you when something important happens on your application."
                                : "You don't have any notifications in this category. Try another filter."
                        }
                    >
                        {category !== 'all' && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setCategory('all')}
                            >
                                Show all
                            </Button>
                        )}
                    </Result>
                ) : (
                    <Stack sx={{ width: '100%' }}>
                        {notifications.map((notification, index) => (
                            <NotificationCard
                                key={notification.id}
                                notification={notification}
                                onNotificationClick={handleNotificationClick}
                                showDivider={index < notifications.length - 1}
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

export default Notifications;
